// Общий на страницу учет загрузки картинок по адресу: загружена, упала или
// еще грузится. Одна загрузка на адрес среди всех, кто грузит через этот
// модуль (списки useImagePreload, предзагрузка выбора в лобби); <img> на
// экране грузит сам — повтор адреса браузер отдаст из своего кэша. Статус
// живет до перезагрузки страницы: загруженное повторный заход на экран не
// грузит заново, а упавшее грузят снова только по явной просьбе
// (isRetryAfterError) — иначе одна просадка сети прятала бы картинку до
// перезагрузки
//
// decode() не ждем — как и useImagesPreloaded: в скрытой вкладке Chrome
// откладывает его до возвращения на вкладку

export const IMAGE_LOAD_STATUS = {
  PENDING: 'pending',
  LOADED: 'loaded',
  ERROR: 'error',
};

const entries = new Map();
const listeners = new Set();

const notify = (src) => {
  listeners.forEach((listener) => listener(src));
};

/**
 * @param {string} src
 * @returns {string|null} статус из IMAGE_LOAD_STATUS; null — загрузку не начинали
 */
export const getImageLoadStatus = (src) => entries.get(src)?.status || null;

/**
 * Загрузка кончилась — успехом или ошибкой. Упавшая картинка тоже не держит
 * ожидание: лучше обойтись без нее, чем ждать вечно
 *
 * @param {string} src
 * @returns {boolean}
 */
export const checkIfImageLoadSettled = (src) => {
  const status = getImageLoadStatus(src);
  return status === IMAGE_LOAD_STATUS.LOADED || status === IMAGE_LOAD_STATUS.ERROR;
};

/**
 * Начинает загрузку, если по этому адресу ее еще не было (или она упала, и
 * просят попробовать снова).
 *
 * @param {string} src
 * @param {Object} [options]
 * @param {'high'|'low'|'auto'} [options.fetchPriority] - приоритет запроса
 *   (подсказка браузеру; где не поддерживается — ни на что не влияет)
 * @param {boolean} [options.isRetryAfterError=false] - упавшую загрузку
 *   начать заново
 * @returns {{image: HTMLImageElement|null, promise: Promise<void>}} картинка
 *   (null — загрузка уже кончилась раньше) и промис ее окончания
 */
export const loadImageOnce = (src, {fetchPriority, isRetryAfterError = false} = {}) => {
  const existing = entries.get(src);
  if (existing && !(isRetryAfterError && existing.status === IMAGE_LOAD_STATUS.ERROR)) {
    return {image: existing.image, promise: existing.promise};
  }

  const image = new Image();
  if (fetchPriority) {
    image.fetchPriority = fetchPriority;
  }
  const entry = {status: IMAGE_LOAD_STATUS.PENDING, image, promise: null};
  entry.promise = new Promise((resolve) => {
    const finish = (status) => {
      entry.status = status;
      // Ссылку на загруженную картинку держит тот, кому она нужна на экране
      // (см. useImagePreload, retain); кэш — только статус
      entry.image = null;
      notify(src);
      resolve();
    };
    image.onload = () => finish(IMAGE_LOAD_STATUS.LOADED);
    image.onerror = () => finish(IMAGE_LOAD_STATUS.ERROR);
  });
  entries.set(src, entry);
  image.src = src;
  notify(src);
  return {image, promise: entry.promise};
};

/**
 * Подписка на смену статусов: слушатель получает адрес, чья загрузка
 * началась или кончилась.
 *
 * @param {function(string): void} listener
 * @returns {function(): void} отписка
 */
export const subscribeImageLoadStatuses = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Забыть все статусы — для тестов: модуль живет между ними
 */
export const clearImageLoadCache = () => {
  entries.clear();
};
